import { MINUTE, SECOND } from '@philo/web-shared';
import { isFunction } from 'lodash';
import React, { lazy } from 'react';
import { retryPromise } from './promise';

const DEFAULT_LOAD_OPTIONS = {
  delay: 1 * SECOND,
  interval: 1 * SECOND,
  retries: (5 * MINUTE) / SECOND,
  timeout: 5 * MINUTE,
};

type AsyncComponent<T> = {
  default: React.ComponentType<T>;
};

const loadOrTimeoutComponent = <T>(
  promise: () => Promise<AsyncComponent<T>>,
  options: Record<'delay' | 'timeout', number>,
) => {
  if (!isFunction(promise)) {
    throw new Error('promise must be a function');
  }

  const minDelay = Number(options?.delay) || DEFAULT_LOAD_OPTIONS.delay;
  const timeout = Number(options?.timeout) || DEFAULT_LOAD_OPTIONS.timeout;

  return Promise.race([
    Promise.all([
      promise(),
      new Promise(resolve => {
        window.setTimeout(resolve, minDelay);
      }),
    ]).then(([module]) => module),
    new Promise<AsyncComponent<T>>((_, reject) => {
      window.setTimeout(() => reject(new Error('The request timed out!')), timeout);
    }),
  ]);
};

export const loadComponent = <T>(
  promise: () => Promise<AsyncComponent<T>>,
  options = DEFAULT_LOAD_OPTIONS,
) => {
  const { delay, timeout, interval, retries } = {
    ...DEFAULT_LOAD_OPTIONS,
    ...options,
  };

  return lazy(() =>
    retryPromise(() => loadOrTimeoutComponent(promise, { delay, timeout }), {
      interval,
      retries,
    }),
  );
};
